// The wire contract of the account routes (plan L-ACC): the public reads the relay serves about
// an account, and the bodies and results of the account actions (register, withdraw,
// append-inbox). The browser and the relay both import these.
//
// Everything here is PUBLIC chain data or a call's own arguments. The account's encryption
// secret never appears: the browser decrypts the inbox itself (Q5).

import { z } from 'zod';

const hex = (bytes: number) => z.string().regex(new RegExp(`^(0x)?[0-9a-fA-F]{${bytes * 2}}$`));
const hex32 = hex(32);
const decimal = z.string().regex(/^[0-9]{1,40}$/);

// ── Reads ─────────────────────────────────────────────────────────────────────

export const AccountStateViewSchema = z.object({
  account: z.string().regex(/^[0-9a-f]{64}$/),
  booted: z.boolean(),
  deviceCount: z.number().int().nonnegative(),
  /** The current device epoch (device entries are derived under it). */
  deviceEpoch: decimal,
  /** Every live device entry (64 hex). A device's use counter is found by matching its entry. */
  devices: z.array(z.string().regex(/^[0-9a-f]{64}$/)),
  /** The nonce the next gated call's challenge binds. */
  authNonce: decimal,
  inboxCount: decimal,
  /** The account's encryption PUBLIC key (X25519, 64 hex). */
  encKey: z.string().regex(/^[0-9a-f]{64}$/),
  /** The account's network salt (its sealed `evm_domain_salt`, 64 hex): every Ed25519 challenge
   *  binds it (../passport/gated.ts `GatedContext`). */
  networkSalt: z.string().regex(/^[0-9a-f]{64}$/),
});
export type AccountStateView = z.infer<typeof AccountStateViewSchema>;

export const InboxPageSchema = z.object({
  account: z.string(),
  from: z.number().int().nonnegative(),
  /** 192-byte entries as hex, in inbox order from `from`; null where an index is empty. */
  entries: z.array(
    z
      .string()
      .regex(/^[0-9a-f]{384}$/)
      .nullable(),
  ),
  total: z.number().int().nonnegative(),
});
export type InboxPage = z.infer<typeof InboxPageSchema>;

export const OwnedOutputSchema = z.object({
  commitment: z.string().regex(/^[0-9a-f]{64}$/),
  mtIndex: decimal,
  txHash: z.string(),
  blockHeight: z.number().int().nonnegative(),
});
export const OwnedInputSchema = z.object({
  nullifier: z.string().regex(/^[0-9a-f]{64}$/),
  txHash: z.string(),
  blockHeight: z.number().int().nonnegative(),
});

/** Every Zswap leaf the ledger inserted for coins the account owns, and every nullifier it spent. */
export const ZswapActivitySchema = z.object({
  account: z.string(),
  outputs: z.array(OwnedOutputSchema),
  inputs: z.array(OwnedInputSchema),
  /** How many of the account's transactions were read. */
  transactions: z.number().int().nonnegative(),
  /** The newest block the answer covers. */
  blockHeight: z.number().int().nonnegative(),
});
export type ZswapActivity = z.infer<typeof ZswapActivitySchema>;

// ── Action bodies ─────────────────────────────────────────────────────────────

export const RegisterPayloadSchema = z
  .object({
    /** The account's encryption PUBLIC key (X25519, 32 bytes). The secret never leaves the browser. */
    encPublicKey: hex32,
  })
  .strict();
export type RegisterPayload = z.infer<typeof RegisterPayloadSchema>;

/** A gated call's own Passport authorisation: the device's Ed25519 signature over the call's
 *  message (built by the account arm's message builder, plan A3/A4), and the device's use counter
 *  (the rolling entry the call consumes, AUTH-9). The relay rebuilds the message from the call's
 *  arguments and the account's state, and checks the signature against the named device (lane B3). */
export const PassportAuthSchema = z
  .object({
    /** The device key (64 lowercase hex) the call is signed by. */
    owner: z.string().regex(/^[0-9a-f]{64}$/),
    /** The Ed25519 signature, 64 bytes (128 lowercase hex). */
    signature: z.string().regex(/^[0-9a-f]{128}$/),
    useCounter: decimal,
  })
  .strict();
export type PassportAuth = z.infer<typeof PassportAuthSchema>;

export const QualifiedCoinSchema = z.object({ nonce: hex32, color: hex32, value: decimal, mtIndex: decimal }).strict();

/** The arm's shielded withdrawal: pay `amount` of `color` from ONE coin (the browser's choice,
 *  L-ACC.5) to a shielded wallet (its coin public key, plus its encryption public key so the
 *  wallet can see the coin: midnight-js refuses a recipient it cannot seal to, upstream Q42). */
export const WithdrawPayloadSchema = z
  .object({
    recipient: hex32,
    /** The recipient wallet's encryption public key (from its shielded address), so the coin's
     *  ciphertext is sealed to it; omitted only when paying the relay's own wallet. */
    recipientEncryptionKey: hex32.optional(),
    color: hex32,
    amount: decimal,
    /** The coin spent, exactly as the challenge binds it (the call's private state). */
    coin: QualifiedCoinSchema,
    /** The auth nonce the signed challenge binds. */
    authNonce: decimal,
    /** AA 00060 P6.3: unsigned metadata (the F3 bytes are unchanged). `bridge-out`: the recipient is a
     *  Bridge-out landing key (I-5), so the relay returns a `landingEntitlement` with the result (one
     *  sponsored tx2, ./bridge/out.ts). Only for a colour the relay's journey registry bridges. */
    purpose: z.literal('bridge-out').optional(),
  })
  .strict();
export type WithdrawPayload = z.infer<typeof WithdrawPayloadSchema>;

/**
 * A single-use APPEND ENTITLEMENT (security review F-B3): the market sponsors an `append-inbox`
 * only for a coin it saw created without a correct inbox entry (a withdrawal's change). The relay
 * issues it in
 * that operation's result, the browser keeps it with the coin, and sends it back to file the
 * entry: `ae1.<account>.<operation id>.<expiry>.<relay MAC>`, all lowercase hex / decimal.
 */
export const APPEND_ENTITLEMENT_PATTERN = /^ae1\.[0-9a-f]{64}\.[0-9a-f]{64}\.[0-9]{1,12}\.[0-9a-f]{64}$/;
export const AppendEntitlementSchema = z.string().regex(APPEND_ENTITLEMENT_PATTERN);

/** The arm's `append_inbox`: file one 192-byte inbox entry (Q13: a withdrawal's change). */
export const AppendInboxPayloadSchema = z
  .object({
    entry: hex(192),
    authNonce: decimal,
    /** The entitlement the market issued for this coin (F-B3). Not part of the signed challenge (the
     *  contract's message is fixed); the relay refuses an append without a valid one. */
    entitlement: AppendEntitlementSchema.optional(),
  })
  .strict();
export type AppendInboxPayload = z.infer<typeof AppendInboxPayloadSchema>;

/**
 * `cancel-offers` (AA 00047 P9.S, audit C6, questions Q30): end EVERY open approval of the account at
 * once, its open offers included, by landing the arm's cheapest nonce-bumping call,
 * `rotate_enc_key_with_ed25519`, with `newKey` = the account's CURRENT encryption key (the state
 * does not change apart from the auth nonce). Authorised by the call's own F3 signature (one prompt);
 * the relay also checks `newKey` is the on-chain key (lane P9.R), so the call can never move the
 * account to another key.
 */
export const CancelOffersPayloadSchema = z
  .object({
    /** The account's encryption public key as it is on chain (64 hex). */
    newKey: hex32,
    /** The auth nonce the signed challenge binds. */
    authNonce: decimal,
  })
  .strict();
export type CancelOffersPayload = z.infer<typeof CancelOffersPayloadSchema>;

export interface CancelOffersResult {
  txId: string;
}

/**
 * `restore-enc-key` (AA 00047 P10, audit round 2 R2-3, questions Q36): an account whose on-chain
 * encryption key is no longer the one this browser holds (a page that passed a real key change off
 * as something else, F-A2-3) gets this browser's key back, with the same circuit as `cancel-offers`,
 * `rotate_enc_key_with_ed25519`, to ANOTHER key: the F3 v2 text reads "Rotate encryption key / New
 * key <16 hex>". Authorised by the call's own signature (one prompt). `newKey` is the BROWSER's
 * encryption public key and differs from the on-chain one (the same key would be a cancel).
 */
export const RestoreEncKeyPayloadSchema = z
  .object({
    /** This browser's encryption public key for the account (64 hex): the key to put back. */
    newKey: hex32,
    /** The auth nonce the signed challenge binds. */
    authNonce: decimal,
  })
  .strict();
export type RestoreEncKeyPayload = z.infer<typeof RestoreEncKeyPayloadSchema>;

export interface RestoreEncKeyResult {
  txId: string;
}

// ── Results (the job's public outcome) ────────────────────────────────────────

export interface RegisterResult {
  account: string;
  /** The device key (64 lowercase hex). */
  device: string;
  txs: { waveOne: string; waveTwo: string; activation: string };
  seconds: { waveOne: number; waveTwo: number; activation: number; total: number };
}

export interface WithdrawResult {
  txId: string;
  /** The change coin the circuit returned (it has no inbox entry yet: Q13). */
  change: { nonce: string; color: string; value: string } | null;
  /** The single-use entitlement to file the change's inbox entry (F-B3), when there is change. */
  changeEntitlement?: string;
  /** AA 00060 P6.3: for `purpose: 'bridge-out'`, the single-use entitlement to ONE sponsored Bridge-out
   *  second transaction (lock, or return to the account) of the landing coin this withdrawal paid. */
  landingEntitlement?: string;
}

export interface AppendInboxResult {
  txId: string;
}
